import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { Menu, X } from "lucide-react";

import { Credit } from "@/components/Credit";
import { useTheme } from "@/contexts/ThemeContext";

const navLinks: { to: string; label: string }[] = [
  { to: "/demo", label: "Analyze" },
  { to: "/samples", label: "Samples" },
  { to: "/docs", label: "How it works" },
  { to: "/about", label: "About" },
];

export function Layout() {
  const theme = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/60 border-b border-slate-800/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {theme.logoUrl ? (
              <img
                src={theme.logoUrl}
                alt={theme.productName}
                className="h-8 w-auto"
              />
            ) : (
              <span className="text-2xl font-extrabold ta-gradient-text tracking-tight">
                {theme.productName}
              </span>
            )}
            {theme.badge && (
              <span className="text-xs uppercase tracking-widest text-slate-500 border border-slate-700/60 rounded-full px-2 py-0.5">
                {theme.badge}
              </span>
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden sm:flex items-center gap-6 text-sm">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/demo"}
                className={({ isActive }) =>
                  `transition ${
                    isActive
                      ? "text-slate-50"
                      : "text-slate-400 hover:text-slate-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {theme.showAnalyticaCredit && (
              <span className="text-xs text-slate-500 hidden sm:block">
                <Credit tier={1} />
              </span>
            )}
            {/* Mobile hamburger */}
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="sm:hidden text-slate-400 hover:text-slate-100 transition p-1"
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <nav className="sm:hidden border-t border-slate-800/60 bg-slate-950/95 backdrop-blur-xl">
            <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col gap-1">
              {navLinks.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/demo"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `py-2.5 px-3 rounded-lg text-sm transition ${
                      isActive
                        ? "text-slate-50 bg-slate-800/50"
                        : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/30"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-slate-800/60 mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-center">
          {theme.showAnalyticaCredit && (
            <Credit tier={3} className="text-slate-400" />
          )}
          <div className="mt-4 text-xs text-slate-600">
            {theme.footerTagline}
          </div>
        </div>
      </footer>
    </div>
  );
}
